/**
 * TrainingRecordListPage — Issue #188.
 *
 * Read-only list of HR training records (signed-up, in-progress, completed).
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_TRAINING_RECORD_STATUS_LABELS,
  useHrTrainingRecords,
  type HrTrainingRecordDto,
} from '@/hooks/useHrIssue188';

function fmt(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

export default function TrainingRecordListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.TRAINING_READ);

  const { items, total, loading, error, fetchRecords } = useHrTrainingRecords();
  const [status, setStatus] = useState<HrTrainingRecordDto['status'] | ''>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchRecords({ status: status || undefined, page, limit });
  }, [canRead, status, page, fetchRecords]);

  if (!canRead) return <AccessDenied resource="培训记录" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">培训记录</h1>
        <p className="text-sm text-slate-500">
          关键岗位与培训挂钩：完成指定培训后由 RBAC 模块发放相关系统权限。
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={status}
            onChange={(e) => {
              setStatus((e.target.value as HrTrainingRecordDto['status']) || '');
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(Object.entries(HR_TRAINING_RECORD_STATUS_LABELS) as [
              HrTrainingRecordDto['status'],
              string,
            ][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">{error.message}</Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full table-fixed text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-3 py-2 w-32">员工 ID</th>
              <th className="px-3 py-2 w-32">课程 ID</th>
              <th className="px-3 py-2 w-24">状态</th>
              <th className="px-3 py-2 w-40">指派时间</th>
              <th className="px-3 py-2 w-40">完成时间</th>
              <th className="px-3 py-2 w-24 text-right">分数</th>
              <th className="px-3 py-2">证书引用</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{row.employeeId}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.courseId}</td>
                <td className="px-3 py-2">{HR_TRAINING_RECORD_STATUS_LABELS[row.status]}</td>
                <td className="px-3 py-2">{fmt(row.assignedAt)}</td>
                <td className="px-3 py-2">{fmt(row.completedAt)}</td>
                <td className="px-3 py-2 text-right">
                  {row.scorePercent !== null ? `${row.scorePercent}%` : '—'}
                </td>
                <td className="px-3 py-2 text-slate-600">{row.certificateRef ?? '—'}</td>
              </tr>
            ))}
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  没有培训记录
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>

      <footer className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </footer>
    </div>
  );
}
